import { Helmet } from "react-helmet-async";

/**
 * SEO meta block — sets <title>, description, Open Graph (FB/WhatsApp/LinkedIn/iMessage),
 * Twitter Card (X), and JSON-LD Schema.org for rich previews and search engines.
 *
 * Usage example:
 *   <SEO
 *     title="Léa Moreau — Mannequin éditorial · Paris"
 *     description="Portfolio premium · Mode & beauté."
 *     image="https://.../photo.jpg"
 *     url="https://helencia.fr/talents/abc"
 *     type="profile"
 *     jsonLd={{ "@context": "https://schema.org", "@type": "Person", ... }}
 *   />
 */
export default function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  jsonLd = null,
}) {
  const safeTitle = title ? `${title} · Helencia` : "Helencia · Plateforme des talents Mode & Beauté";
  const safeDescription = description || "Helencia connecte les talents éditoriaux aux marques, créateurs et photographes. Portfolio premium, concours et collaborations.";
  const canonical = url || (typeof window !== "undefined" ? window.location.href : undefined);
  const fallbackImage = (typeof window !== "undefined" ? `${window.location.origin}/helencia-logo-256.png` : null);
  const ogImage = image || fallbackImage;

  return (
    <Helmet>
      <title>{safeTitle}</title>
      <meta name="description" content={safeDescription} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph (Facebook, WhatsApp, LinkedIn, Discord, iMessage, Telegram, Slack) */}
      <meta property="og:site_name" content="Helencia" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDescription} />
      {canonical && <meta property="og:url" content={canonical} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:alt" content={safeTitle} />}
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter / X */}
      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* JSON-LD Schema.org for Google rich results */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
